import { useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { useTranslation } from 'react-i18next';
import { type PlaceDetails } from 'GooglePlaces';
import { FilterControls } from './FilterControls';
import { ScrollFadeContainer } from '../ScrollFadeContainer';
import { type FilterState, type FilterTab } from '../../hooks/usePeopleFilters.types';
import { hasActiveFiltersInState } from '../../hooks/usePeopleFilters.utils';
import { INITIAL_FILTER_STATE } from '../../peopleFiltersSlice';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type MobileFiltersScreenProps = {
    filterState: FilterState;
    onApply: (state: FilterState) => void;
    onClose: () => void;
};

// oxlint-disable-next-line max-lines-per-function -- mobile filters with draft buffering
export function MobileFiltersScreen({ filterState, onApply, onClose }: MobileFiltersScreenProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.filters' });

    // Draft state — cloned from live state on mount; edits stay local until Apply
    const [draft, setDraft] = useState<FilterState>(() => ({ ...filterState }));

    const setDraftField = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
        setDraft(prev => ({ ...prev, [key]: value }));
    }, []);

    const hasActiveFilters = useMemo(() => hasActiveFiltersInState(draft), [draft]);

    const resetAndApply = useCallback(() => {
        const reset = { ...INITIAL_FILTER_STATE, showAdvanced: draft.showAdvanced };
        onApply(reset);
        onClose();
    }, [draft.showAdvanced, onApply, onClose]);

    const handleApply = useCallback(() => {
        onApply(draft);
        onClose();
    }, [draft, onApply, onClose]);

    return (
        <div className="flex h-full flex-col bg-white">
            {/* Header: back chevron + FILTERS title */}
            <div className="flex items-center px-4 pb-4 pt-3">
                <button type="button" onClick={onClose} className="flex items-center text-primary">
                    <FontAwesomeIcon icon={faChevronLeft} className="text-lg" />
                </button>
                <span className="flex-1 text-center text-lg font-semibold uppercase text-black">
                    {t('filtersTitle')}
                </span>
                {/* Spacer to balance the back button */}
                <div className="w-5" />
            </div>

            {/* Filter controls — operating on draft state */}
            <ScrollFadeContainer className="flex-1 overflow-y-auto px-4">
                <FilterControls
                    keywordSearch={draft.keywordSearch}
                    onKeywordSearchChange={(v: string) => setDraftField('keywordSearch', v)}
                    activeTab={draft.activeTab}
                    onTabChange={(v: FilterTab) => setDraftField('activeTab', v)}
                    showAdvanced={draft.showAdvanced}
                    onShowAdvancedChange={(v: boolean) => setDraftField('showAdvanced', v)}
                    places={draft.places}
                    onPlacesChange={(v: PlaceDetails[]) => setDraftField('places', v)}
                    programTypes={draft.programTypes}
                    onProgramTypesChange={(v: string[]) => setDraftField('programTypes', v)}
                    companies={draft.companies}
                    onCompaniesChange={(v: string[]) => setDraftField('companies', v)}
                    industries={draft.industries}
                    onIndustriesChange={(v: string[]) => setDraftField('industries', v)}
                    interests={draft.interests}
                    onInterestsChange={(v: string[]) => setDraftField('interests', v)}
                    lookingFor={draft.lookingFor}
                    onLookingForChange={(v: string[]) => setDraftField('lookingFor', v)}
                    timezones={draft.timezones}
                    onTimezonesChange={(v: string[]) => setDraftField('timezones', v)}
                    hideSearch
                    hasActiveFilters={hasActiveFilters}
                    onReset={resetAndApply}
                />
            </ScrollFadeContainer>

            {/* Apply Filters button — pinned at bottom, with clearance for mobile nav + safe area */}
            <div className="px-4 pb-4 pt-2">
                <button
                    type="button"
                    onClick={handleApply}
                    className="h-12 w-full rounded-[12px] bg-black text-base font-medium text-white"
                >
                    {t('applyFilters')}
                </button>
            </div>
        </div>
    );
}
